import { useState } from 'react';

import Combobox from '@perpay-web/fintech/components/base/Combobox/Combobox';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { getClassName } from '@perpay-web/utils/domUtils';
import {
    EndToEndTestIdProvider,
    getTestIdProps,
} from '@perpay-web/utils/endToEndTestIds';
import { isEnterOrSpaceKey } from '@perpay-web/utils/keyboardUtils';

import styles from './CompanyPicker.scss';

function renderItems(title, renderProps, previousLength, items) {
    if (items.length === 0) {
        return null;
    }

    const {
        getHighlightedCss,
        getItemId,
        getResultCss,
        getSelectedCss,
        highlightedIndex,
        onHighlightIndex,
        onSelect,
        selectedItem,
    } = renderProps;

    return (
        <>
            {title ? <p className={styles.results__group}>{title}</p> : null}
            <ul>
                {items.map((item, index) => (
                    <li
                        id={getItemId(index)}
                        {...getTestIdProps(`company-combo-box-name-${index}`)}
                        key={item.text}
                        className={getClassName(
                            getResultCss(),
                            selectedItem === item.text
                                ? getSelectedCss()
                                : null,
                            highlightedIndex === previousLength + index
                                ? getHighlightedCss()
                                : null,
                        )}
                        role='option'
                        aria-selected={
                            item ? item.name === highlightedIndex : null
                        }
                        onMouseOver={() =>
                            onHighlightIndex(previousLength + index)
                        }
                        onClick={() => {
                            onSelect(item.text);
                        }}
                        // This onKeyDown shouldn't get called, but the
                        // linter requires it. It won't get called because
                        // the focus remains on the input above while the
                        // user keys down. If we give these tabindex="0"
                        // then this is needed.
                        onKeyDown={(event) => {
                            if (isEnterOrSpaceKey(event.key)) {
                                onSelect(item.text);
                            }
                        }}
                        onFocus={() => {
                            onHighlightIndex(index);
                        }}
                    >
                        {item.text}
                    </li>
                ))}
            </ul>
        </>
    );
}

const CompanyPicker = ({
    error = '',
    topMatches,
    matches,
    suggestedMatches,
    initialValue = '',
    isLoadingResults,
    showAddOption = true,
    possibleMatches,
    hasLoadedResults,
    onInputValueChange,
    onSelect,
    onSelectSelfOnboarding,
    onUnmount,
    inputComponent,
}) => {
    const [inputValue, setInputValue] = useState('');
    const { isMobile } = useIsMobile();
    useMountAndUnmount(() => onUnmount);

    const shouldShowComboboxOptions = () => {
        // We only perform a search if the user types more than 3 characters, so
        // We should only show the combobox options under the following conditions:

        // 1. If there are exactly three characters, show only if there are company results
        if (inputValue.length === 3) {
            return (
                topMatches.length + matches.length + suggestedMatches.length > 0
            );
        }

        // 2. The input value is 3 or more characters
        if (inputValue.length > 3) {
            return true;
        }

        // 3. If there are less than 3 characters, hide
        return false;
    };

    const getSelfOnboardingCTAText = () =>
        `${
            isMobile ? 'Tap' : 'Click'
        } here to add your company: "${inputValue}"`;

    const onInputValueChangeInternal = (value) => {
        setInputValue(value);
        onInputValueChange(value);
    };

    const selfOnboardingMatch = {
        text: getSelfOnboardingCTAText(),
        name: inputValue,
    };
    const allItems = [
        ...topMatches,
        ...matches,
        ...suggestedMatches,
        selfOnboardingMatch,
    ];
    const { length } = allItems;

    const onSelectInternal = (text) => {
        if (text === getSelfOnboardingCTAText()) {
            onSelectSelfOnboarding(inputValue);
            return;
        }

        for (let i = 0; i < allItems.length; i += 1) {
            const item = allItems[i];
            if (item.text === text) {
                onSelect({
                    company: item.text,
                    uuid: item.uuid,
                    isGigEconomyCompany: item.isGigEconomyCompany,
                    hasPerpaySplitPayroll: item.hasPerpaySplitPayroll,
                });
                break;
            }
        }
    };

    const showEmptyResults =
        inputValue.length >= 3 &&
        ((hasLoadedResults && possibleMatches === 0) || !isLoadingResults);
    const maybeRenderEmptyResults = (renderProps) =>
        showEmptyResults && showAddOption
            ? renderItems("Don't see your company?", renderProps, length - 1, [
                  selfOnboardingMatch,
              ])
            : null;

    return (
        <div>
            <EndToEndTestIdProvider testId='company-combo-box'>
                <Combobox
                    id='company-name'
                    itemCount={length}
                    placeholder="I'm paid by..."
                    onSelect={(value) => onSelectInternal(value)}
                    onInputValueChange={(value) =>
                        onInputValueChangeInternal(value)
                    }
                    getValueAtIndex={(index) => allItems[index]}
                    loading={isLoadingResults}
                    initialValue={initialValue}
                    inputComponent={inputComponent}
                >
                    {(renderProps) => (
                        <>
                            {inputValue.length < 3 && inputValue.length > 0 ? (
                                <p className={styles.typing}>
                                    Please continue typing for a more accurate
                                    match
                                </p>
                            ) : null}
                            {shouldShowComboboxOptions() ? (
                                <>
                                    {hasLoadedResults ? (
                                        <p
                                            className={
                                                styles['possible-matches']
                                            }
                                        >
                                            {possibleMatches} possible
                                            matches...
                                        </p>
                                    ) : null}
                                    {renderItems(
                                        'Top matches',
                                        renderProps,
                                        0,
                                        topMatches,
                                    )}
                                    {renderItems(
                                        'All companies',
                                        renderProps,
                                        topMatches.length,
                                        matches,
                                    )}
                                    {renderItems(
                                        'Suggested companies',
                                        renderProps,
                                        topMatches.length + matches.length,
                                        suggestedMatches,
                                    )}
                                </>
                            ) : null}
                            {maybeRenderEmptyResults(renderProps)}
                        </>
                    )}
                </Combobox>
            </EndToEndTestIdProvider>
            {error ? (
                <p className={styles.error}>An error occurred: {error}</p>
            ) : null}
        </div>
    );
};

export default CompanyPicker;
